import { DependencyList, useEffect, useRef } from 'react';
import { useAlertContext } from '@/core/providers/AlertContext';
import { GlobalAlertOptions } from '@/components/AppAlert/alert.types';

export interface UseAlertOptions extends GlobalAlertOptions {
  dependencies: DependencyList;
  condition: () => boolean;
}

function useAlert(hookOptions: UseAlertOptions) {
  const { condition, dependencies, ...globalOptions } = hookOptions;
  const {
    custom: { closable = true, onClose },
    mui: muiAlertProps,
  } = globalOptions;
  const { setIsAlertActive, setContextAlertOptions } = useAlertContext();
  const initialPath = useRef(window.location.pathname);

  const handleNavigationChange = () => {
    if (window.location.pathname !== initialPath.current) {
      setIsAlertActive(false);
    }
  };

  useEffect(() => {
    if (condition()) {
      if (closable && !muiAlertProps.onClose) {
        muiAlertProps.onClose = () => {
          if (onClose) {
            onClose();
          }
          setIsAlertActive(false);
        };
      }

      setContextAlertOptions(globalOptions);
      setIsAlertActive(true);
    }
  }, dependencies);

  useEffect(() => {
    const handlePopState = () => handleNavigationChange();
    const handleLocationChange = () => handleNavigationChange();

    window.addEventListener('popstate', handlePopState);
    window.addEventListener('locationchange', handleLocationChange);

    return () => {
      window.removeEventListener('popstate', handlePopState);
      window.removeEventListener('locationchange', handleLocationChange);
    };
  }, []);
}

export default useAlert;
